import {
  AddToCartUnitType,
  type TileVariant,
  variantUomFromCartUnit,
} from '@fdc/components/ui/product-tile/add-to-cart-helpers';
import { ProductSummaryUnion } from '@fdc/types';

type VariantSummaries = ProductSummaryUnion['variants'];
type VariantSummary = NonNullable<VariantSummaries>[number];

export const getVariantByUom = (variants: VariantSummaries, uom: string) =>
  variants?.find((v) => v?.unitOfMeasure?.toLowerCase() === uom);

/**
 * `purchaseUnit.unit` isn't always populated on this GraphQL type — falls
 * back to the variant-level `unitOfMeasure` field.
 */
const toPurchasingUnits = (
  variant: VariantSummary
): TileVariant['purchasingUnits'] => {
  if (!variant.purchaseUnit) {
    return undefined;
  }
  return {
    ...variant.purchaseUnit,
    unit: variant.purchaseUnit.unit ?? variant.unitOfMeasure,
  };
};

/** Adapts `VariantSummary[]` to the minimal `TileVariant[]` shape used by the product-tile family. */
export const toTileVariants = (variants: VariantSummaries): TileVariant[] =>
  (variants ?? []).flatMap((variant) =>
    variant?.variantKey
      ? [
          {
            key: variant.variantKey,
            purchasingUnits: toPurchasingUnits(variant),
          },
        ]
      : []
  );

export const getUomFromVariantKey = (
  variants: VariantSummaries,
  variantKey?: string | null
) => variants?.find((v) => v?.variantKey === variantKey)?.unitOfMeasure;

/** Active variant for the tile `unitOfMeasure` / toggle type. */
export const resolveActiveVariant = (
  variants: VariantSummaries,
  unitOfMeasure: string,
  selectedType: AddToCartUnitType
) =>
  getVariantByUom(
    variants,
    variantUomFromCartUnit(unitOfMeasure, selectedType)
  );

/** Variant key for the opposite unit of measure (each <-> kg). */
export const resolveAlternativeVariantKey = (
  variants: VariantSummaries,
  unitOfMeasure: string,
  selectedType: AddToCartUnitType
) => {
  const targetUom = variantUomFromCartUnit(unitOfMeasure, selectedType);
  const otherUom = targetUom === 'kg' ? 'each' : 'kg';
  return getVariantByUom(variants, otherUom)?.variantKey;
};
